import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { useAuth } from "./AuthContext";
import {
  PORTAL_TICKET_ACCESS_COLLECTION, PORTAL_TICKETS_COLLECTION, normalizePortalTicketEmail,
  portalTicketTypesFromAccess, portalTicketTime, validPortalTicketEmail,
} from "../jsx/components/PatientPortal/patientPortalTicketUtils";

const Context = createContext({ loading: true, ticketTypes: [], tickets: [], error: "", refreshing: false });
export const usePatientPortalTickets = () => useContext(Context);

export const PatientPortalTicketsProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const subject = currentUser ? `${currentUser.uid}:${normalizePortalTicketEmail(currentUser.email)}` : "";
  const email = normalizePortalTicketEmail(currentUser && currentUser.email);
  const [access, setAccess] = useState({ subject: "", loading: true, types: [], error: "" });
  const [rows, setRows] = useState({ key: "", byType: {}, loaded: [], errors: {} });
  const [refreshState, setRefreshState] = useState({ key: "", pending: false, error: "" });

  useEffect(() => {
    if (!currentUser || !validPortalTicketEmail(email)) {
      setAccess({ subject, loading: false, types: [], error: "" });
      return undefined;
    }
    return onSnapshot(doc(db, PORTAL_TICKET_ACCESS_COLLECTION, email), (snapshot) => {
      setAccess({ subject, loading: false, types: portalTicketTypesFromAccess(snapshot.data()), error: "" });
    }, () => setAccess({ subject, loading: false, types: [], error: "Unable to load ticket access. Please try again." }));
  }, [subject, email]); // Identity changes clear access before old data can render.

  const typesKey = access.subject === subject ? access.types.join(",") : "";
  const key = `${subject}:${typesKey}`;
  const currentKey = useRef(key);
  currentKey.current = key;
  const ticketTypes = useMemo(() => typesKey ? typesKey.split(",") : [], [typesKey]);
  useEffect(() => {
    let active = true;
    setRows({ key, byType: {}, loaded: [], errors: {} });
    const unsubscribes = ticketTypes.map((type) => onSnapshot(
      query(collection(db, PORTAL_TICKETS_COLLECTION), where("serviceId", "==", type)),
      (snapshot) => {
        if (!active) return;
        setRows((previous) => ({
          key, byType: { ...previous.byType, [type]: snapshot.docs.map((row) => ({ ...row.data(), id: row.id })) },
          loaded: Array.from(new Set([...previous.loaded, type])), errors: { ...previous.errors, [type]: "" },
        }));
      }, () => {
        if (!active) return;
        setRows((previous) => ({ key, byType: { ...previous.byType, [type]: [] }, loaded: Array.from(new Set([...previous.loaded, type])), errors: { ...previous.errors, [type]: "Unable to load patient tickets. Please try again." } }));
      },
    ));
    return () => { active = false; unsubscribes.forEach((unsubscribe) => unsubscribe()); };
  }, [key, ticketTypes]);

  const refresh = useCallback(async (isActive = () => true) => {
    const canContinue = () => currentKey.current === key && isActive();
    setRefreshState({ key, pending: true, error: "" });
    try {
      const sync = httpsCallable(functions, "refreshPatientPortalTickets", { timeout: 540000 });
      for (const serviceId of ticketTypes) {
        let cursor = null;
        do {
          if (!canContinue()) return;
          const result = await sync({ serviceId, cursor });
          cursor = result.data.cursor;
        } while (cursor);
      }
      if (canContinue()) setRefreshState({ key, pending: false, error: "" });
    } catch (error) {
      if (canContinue()) setRefreshState({ key, pending: false, error: "Unable to sync portal tickets. Use Refresh to retry." });
    }
  }, [key, ticketTypes]);

  useEffect(() => {
    if (!ticketTypes.length) return undefined;
    let active = true;
    refresh(() => active);
    return () => { active = false; };
  }, [refresh, ticketTypes]);

  const tickets = useMemo(() => rows.key !== key ? [] : ticketTypes
    .reduce((all, type) => all.concat(rows.byType[type] || []), [])
    .sort((a, b) => portalTicketTime(b.createdAt) - portalTicketTime(a.createdAt) || a.id.localeCompare(b.id)), [rows, key, ticketTypes]);
  const loading = access.subject !== subject || access.loading || (ticketTypes.length > 0 && (rows.key !== key || rows.loaded.length < ticketTypes.length));
  const error = (access.subject === subject && access.error) || (rows.key === key && Object.values(rows.errors).find(Boolean)) || (refreshState.key === key && refreshState.error) || "";

  return <Context.Provider value={{ accessKey: key, loading, ticketTypes, tickets, error, refreshing: refreshState.key === key && refreshState.pending, refresh }}>
    {children}
  </Context.Provider>;
};
